/**
 * LandingHero - Two-column hero with phone mockup
 * Left: text + CTA | Right: WhatsApp phone mockup
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Typography, Box, Button, Container, Stack, Chip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import { COLORS, GRADIENTS, TRANSITIONS, RADIUS, TYPOGRAPHY } from './landingTheme';

// Decorative mesh background
const MeshGradient = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      background: `
        radial-gradient(circle at 20% 30%, rgba(245, 158, 11, 0.08) 0%, transparent 40%),
        radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.06) 0%, transparent 35%),
        radial-gradient(circle at 60% 80%, rgba(16, 185, 129, 0.05) 0%, transparent 40%)
      `,
      pointerEvents: 'none',
    }}
  />
);

// Floating WhatsApp phone mockup with chat
const PhoneMockup = () => (
  <Box
    sx={{
      width: { xs: 260, sm: 280, md: 300 },
      mx: 'auto',
      position: 'relative',
      // Glow behind phone
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '10%', left: '-15%', right: '-15%', bottom: '5%',
        background: 'radial-gradient(ellipse, rgba(245, 158, 11, 0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(40px)',
        pointerEvents: 'none',
      },
      // Entry animation
      animation: 'phoneFloat 6s ease-in-out infinite',
      '@keyframes phoneFloat': {
        '0%, 100%': { transform: 'translateY(0)' },
        '50%': { transform: 'translateY(-12px)' },
      },
    }}
  >
    {/* Phone frame */}
    <Box
      sx={{
        borderRadius: '32px',
        border: '3px solid rgba(255,255,255,0.15)',
        background: '#f8fafc',
        overflow: 'hidden',
        boxShadow: '0 30px 80px rgba(0,0,0,0.35), 0 10px 30px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)',
        position: 'relative',
      }}
    >
      {/* Status bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 0.75, background: '#075e54' }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[1, 2, 3].map(i => (
            <Box key={i} sx={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.5)' }} />
          ))}
        </Box>
        <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>12:30</Typography>
        <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
          <Box sx={{ width: 12, height: 6, borderRadius: 0.5, border: '1px solid rgba(255,255,255,0.5)' }} />
        </Box>
      </Box>

      {/* WhatsApp header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 2, py: 1.2, background: '#075e54' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #25D366, #128C7E)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SmartToyIcon sx={{ fontSize: 18, color: '#fff' }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#fff' }}>Dra. García - NARA</Typography>
          <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>en línea</Typography>
        </Box>
      </Box>

      {/* Chat area */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, background: '#ece5dd', minHeight: 300 }}>
        {/* User message */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ background: '#dcf8c6', px: 1.5, py: 0.8, borderRadius: '10px 10px 3px 10px', maxWidth: '78%', boxShadow: '0 1px 1px rgba(0,0,0,0.08)' }}>
            <Typography sx={{ fontSize: 11, color: '#1a1a1a', lineHeight: 1.45 }}>Me duele la cabeza desde ayer y tengo algo de fiebre</Typography>
            <Typography sx={{ fontSize: 8, color: '#999', textAlign: 'right', mt: 0.3 }}>12:30 ✓✓</Typography>
          </Box>
        </Box>

        {/* Bot response */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Box sx={{ background: '#fff', px: 1.5, py: 0.8, borderRadius: '10px 10px 10px 3px', maxWidth: '85%', boxShadow: '0 1px 1px rgba(0,0,0,0.08)' }}>
            <Typography sx={{ fontSize: 11, color: '#1a1a1a', lineHeight: 1.45 }}>
              Entiendo tu preocupación 💚 Voy a analizar tus síntomas:
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#1a1a1a', lineHeight: 1.6, mt: 0.5, whiteSpace: 'pre-line' }}>
              {'📋 Causas posibles:\n1. Gripe o resfriado común\n2. Sinusitis aguda\n3. Cefalea tensional'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#1a1a1a', lineHeight: 1.45, mt: 0.5 }}>
              🩺 Te recomiendo consulta si la fiebre supera 38.5°C
            </Typography>
            <Typography sx={{ fontSize: 8, color: '#999', textAlign: 'right', mt: 0.3 }}>12:31</Typography>
          </Box>
        </Box>

        {/* User reply */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ background: '#dcf8c6', px: 1.5, py: 0.8, borderRadius: '10px 10px 3px 10px', boxShadow: '0 1px 1px rgba(0,0,0,0.08)' }}>
            <Typography sx={{ fontSize: 11, color: '#1a1a1a', lineHeight: 1.45 }}>Quiero agendar una cita para mañana</Typography>
            <Typography sx={{ fontSize: 8, color: '#999', textAlign: 'right', mt: 0.3 }}>12:31 ✓✓</Typography>
          </Box>
        </Box>

        {/* Bot with slots */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Box sx={{ background: '#fff', px: 1.5, py: 0.8, borderRadius: '10px 10px 10px 3px', boxShadow: '0 1px 1px rgba(0,0,0,0.08)' }}>
            <Typography sx={{ fontSize: 11, color: '#1a1a1a', lineHeight: 1.45 }}>
              Estos son los horarios disponibles para mañana:
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#1a1a1a', lineHeight: 1.7, mt: 0.5, whiteSpace: 'pre-line' }}>
              {'✅ 9:00 AM\n✅ 10:30 AM\n✅ 2:00 PM'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#1a1a1a', lineHeight: 1.45, mt: 0.3 }}>
              ¿Cuál prefieres? 🤗
            </Typography>
            <Typography sx={{ fontSize: 8, color: '#999', textAlign: 'right', mt: 0.3 }}>12:31</Typography>
          </Box>
        </Box>
      </Box>

      {/* Input bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, background: '#f0f0f0' }}>
        <Box sx={{ flex: 1, py: 0.7, px: 1.5, borderRadius: 3, background: '#fff', border: '1px solid #e0e0e0' }}>
          <Typography sx={{ fontSize: 10, color: '#999' }}>Escribe un mensaje...</Typography>
        </Box>
        <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <WhatsAppIcon sx={{ fontSize: 16, color: '#fff' }} />
        </Box>
      </Box>
    </Box>
  </Box>
);


export default function LandingHero({ onOpenLogin }) {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 12, md: 14 },
        pb: { xs: 4, md: 6 },
        background: GRADIENTS.hero,
      }}
    >
      <MeshGradient />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Two-column layout: text left, phone right */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 4, md: 6 },
            alignItems: 'center',
            mb: { xs: 2, md: 0 },
          }}
        >
          {/* Left: text content */}
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            {/* Badge */}
            <Chip
              label="Diagnóstico con IA + Gestión de Citas"
              sx={{
                mb: 3,
                px: 1.5,
                py: 2,
                fontSize: TYPOGRAPHY.small,
                fontWeight: 600,
                background: 'rgba(245, 158, 11, 0.12)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(245, 158, 11, 0.25)',
                color: '#fbbf24',
                borderRadius: RADIUS.full,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />

            {/* Headline */}
            <Typography
              component="h1"
              sx={{
                fontSize: TYPOGRAPHY.h1,
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                color: '#fff',
                mb: 2,
              }}
            >
              Tu{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                asistente médico IA
              </Box>
              {' '}en WhatsApp
            </Typography>

            {/* Subheadline */}
            <Typography
              sx={{
                fontSize: TYPOGRAPHY.bodyLg,
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: 1.7,
                maxWidth: { xs: 540, md: 460 },
                mx: { xs: 'auto', md: 0 },
                mb: 4,
              }}
            >
              Atiende pacientes 24/7 con inteligencia artificial. Diagnostica síntomas,
              agenda citas y recuerda el historial de cada paciente — todo desde WhatsApp.
            </Typography>

            {/* CTA */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent={{ xs: 'center', md: 'flex-start' }}
              alignItems="center"
            >
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                endIcon={<ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
                sx={{
                  background: GRADIENTS.primaryButton,
                  color: '#0f2440',
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.3,
                  fontSize: '0.95rem',
                  borderRadius: RADIUS.md,
                  boxShadow: '0 4px 20px rgba(245, 158, 11, 0.35)',
                  transition: TRANSITIONS.normal,
                  textTransform: 'none',
                  '&:hover': {
                    background: GRADIENTS.primaryButtonHover,
                    boxShadow: '0 8px 30px rgba(245, 158, 11, 0.45)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Comenzar gratis
              </Button>

              <Button
                component="a"
                href="https://wa.me/573052608968?text=Hola%2C%20me%20interesa%20una%20demo%20de%20NARA%20para%20mi%20consultorio"
                target="_blank"
                rel="noopener"
                variant="outlined"
                startIcon={<WhatsAppIcon sx={{ fontSize: '18px !important' }} />}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  fontWeight: 500,
                  px: 3,
                  py: 1.2,
                  fontSize: '0.95rem',
                  borderRadius: RADIUS.md,
                  transition: TRANSITIONS.normal,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    background: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                Solicitar demo
              </Button>
            </Stack>

            {/* Trust badge */}
            <Typography
              sx={{
                mt: 3,
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: TYPOGRAPHY.small,
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Demo gratuita · 20 conversaciones por 15 días · HIPAA Compliant
            </Typography>
          </Box>

          {/* Right: phone mockup — desktop (full size) */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <PhoneMockup />
          </Box>
        </Box>

        {/* Phone mockup — mobile only (smaller, centered) */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'center',
            mt: 2,
            mb: 1,
            transform: 'scale(0.82)',
            transformOrigin: 'top center',
          }}
        >
          <PhoneMockup />
        </Box>

      </Container>
    </Box>
  );
}
